import { useMemo, useState } from 'react';
import { useMatches } from '@/data/use-matches';
import { useParticipants } from '@/data/use-participants';
import { computePlaces, placeMap } from '@/engine';
import { MatchInputDialog } from '@/features/match-input/match-input-dialog';
import { MatchCard } from '@/features/bracket-view/match-card';
import { LoadingState, ErrorState } from '@/components/design';
import type { Match, Tournament } from '@/data/types';

interface Props {
  tournament: Tournament;
}

function MatchSection({
  title,
  matches,
  placeByParticipant,
  onSelect,
}: {
  title: string;
  matches: Match[];
  placeByParticipant: ReturnType<typeof placeMap>;
  onSelect: (m: Match) => void;
}) {
  return (
    <section>
      <div className="row gap-3" style={{ marginBottom: 14 }}>
        <div className="td-h3">{title}</div>
        <span className="t-mono t-mute" style={{ fontSize: 13 }}>
          {matches.length}
        </span>
      </div>
      {matches.length === 0 ? (
        <div
          className="t-small t-soft"
          style={{
            padding: 16,
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-3)',
            textAlign: 'center',
          }}
        >
          Здесь пусто
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))',
            gap: 12,
          }}
        >
          {matches.map((m) => (
            <MatchCard
              key={m.id}
              match={m}
              onClick={() => onSelect(m)}
              placeByParticipant={placeByParticipant}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function MatchesTab({ tournament }: Props) {
  const { matches, error } = useMatches(tournament.id);
  const { participants } = useParticipants(tournament.id);
  const [selected, setSelected] = useState<Match | null>(null);

  const placeByParticipant = useMemo(
    () =>
      placeMap(
        computePlaces(
          matches ?? [],
          (participants ?? []).map((p) => p.id),
          tournament.format,
          tournament.groups
        )
      ),
    [matches, participants, tournament.format, tournament.groups]
  );

  if (error) return <ErrorState title="Ошибка" body={error.message} />;
  if (matches === undefined) return <LoadingState />;

  const ready = matches.filter((m) => m.status === 'READY');
  const completed = matches.filter((m) => m.status === 'COMPLETED').slice().reverse();

  return (
    <div className="space-y-8">
      <MatchSection
        title="Готовы к игре"
        matches={ready}
        placeByParticipant={placeByParticipant}
        onSelect={setSelected}
      />
      <MatchSection
        title="Сыграны"
        matches={completed}
        placeByParticipant={placeByParticipant}
        onSelect={setSelected}
      />

      <MatchInputDialog
        tournamentId={tournament.id}
        match={
          selected && (selected.status === 'READY' || selected.status === 'COMPLETED')
            ? selected
            : null
        }
        matchFormat={tournament.matchFormat}
        scoreMode={tournament.scoreMode ?? 'SETS'}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
